/**
 * The tick simulation. `step(state) -> state` is a pure function over plain
 * data: no timers, no randomness, no wall clock (docs/01 4.1).
 *
 * The five phases below are docs/02 3.2, in order and with the same names.
 * Where a comment says "docs/02 X", the rule is transcribed, not invented.
 * The Python reference simulator (tools/check_levels.py) is the oracle; the
 * traces in test/ must match it tick for tick.
 */
import { mix, MUD, type ColorId } from '../colors/mix.ts';
import {
  canEnter,
  crossLane,
  delta,
  opposite,
  pairedPort,
  rotateSide,
  type Rotation,
  type Side,
} from './tiles.ts';
import type { Blob, FailInfo, Level, Placement, SimState } from './types.ts';

function key(x: number, y: number): string {
  return `${x},${y}`;
}

export function tileAt(state: SimState, x: number, y: number): Placement | null {
  if (y < 0 || y >= state.level.grid.height || x < 0 || x >= state.level.grid.width) return null;
  return state.board[y]?.[x] ?? null;
}

/** Build the initial running state from a level plus the player's placements. */
export function createSim(level: Level, placements: Placement[]): SimState {
  const board: (Placement | null)[][] = [];
  for (let y = 0; y < level.grid.height; y++) {
    board.push(new Array<Placement | null>(level.grid.width).fill(null));
  }
  const put = (p: Placement): void => {
    const row = board[p.y];
    if (row) row[p.x] = p;
  };
  level.fixed.forEach(put);
  placements.forEach(put);

  const vats = level.fixed
    .filter((p) => p.tile === 'vat')
    .map((p) => ({ placement: p, remaining: p.cans ?? 0 }));
  const buckets = level.fixed
    .filter((p) => p.tile === 'bucket')
    .map((p) => ({ placement: p, received: 0 }));

  const splitters: Record<string, 'cw' | 'ccw'> = {};
  for (const row of board) {
    for (const p of row) {
      // docs/02 2.7: `next` is initialised to cw when PLAY is pressed, so
      // every run starts identically.
      if (p && p.tile === 'splitter') splitters[key(p.x, p.y)] = 'cw';
    }
  }

  return {
    level,
    board,
    tick: 0,
    blobs: [],
    vats,
    buckets,
    splitters,
    status: 'running',
    nextBlobId: 1,
    seen: new Set<string>(),
  };
}

/**
 * State fingerprint for `stuck` detection (docs/02 3.5).
 *
 * The per-vat emission countdown MUST be included. Without it, a blob held in
 * a mixer while another vat waits for its `start` tick looks like a repeat on
 * the very next tick and is wrongly reported stuck.
 */
function fingerprint(s: SimState, tick: number): string {
  const blobs = s.blobs
    .map((b) => `${b.color}:${b.units}:${b.x}:${b.y}:${b.entry ?? '-'}:${b.exit ?? '-'}`)
    .sort()
    .join('|');
  const vats = s.vats
    .map((v) => {
      if (v.remaining <= 0) return `${v.placement.x},${v.placement.y}:0:-`;
      const start = v.placement.start ?? 1;
      const every = v.placement.every ?? 1;
      // Ticks until this vat's next emission.
      let next = start;
      while (next <= tick) next += every;
      return `${v.placement.x},${v.placement.y}:${v.remaining}:${next - tick}`;
    })
    .join('|');
  const buckets = s.buckets.map((b) => `${b.placement.x},${b.placement.y}:${b.received}`).join('|');
  const spl = Object.entries(s.splitters)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${k}:${v}`)
    .join('|');
  return `${blobs}#${vats}#${buckets}#${spl}`;
}

function failState(s: SimState, fail: FailInfo, tick: number): SimState {
  return { ...s, status: 'failed', fail, tick };
}

/**
 * One tick. Returns a new state; never mutates the input.
 *
 * `expandable` is the solver's hook (docs/03 7.1): when true, a blob about to
 * enter an EMPTY in-grid cell does not fail with `leak`; the run pauses with
 * status `blocked` and `pending` naming the cell and the side the blob needs.
 * The solver branches there over the tiles that could accept it, then resumes
 * this exact state. The game always calls step() with expandable = false, so
 * player-facing behaviour is unchanged and the solver and the game share one
 * set of rules rather than two implementations that can drift.
 */
export function step(prev: SimState, expandable = false): SimState {
  if (prev.status !== 'running') return prev;

  const tick = prev.tick + 1;
  const level = prev.level;

  // Work on copies; the caller's state stays untouched.
  let blobs: Blob[] = prev.blobs.map((b) => ({ ...b }));
  const vats = prev.vats.map((v) => ({ ...v }));
  const buckets = prev.buckets.map((b) => ({ ...b }));
  const splitters = { ...prev.splitters };
  let nextBlobId = prev.nextBlobId;
  const state: SimState = { ...prev, blobs, vats, buckets, splitters, nextBlobId, tick };

  // ---------------------------------------------------------------- PHASE 1
  // EMIT. Vats in reading order (y, then x). The emitted blob moves into the
  // outlet neighbour on this same tick's ADVANCE, so a vat never holds a blob.
  const vatsInReadingOrder = [...vats].sort(
    (a, b) => a.placement.y - b.placement.y || a.placement.x - b.placement.x,
  );
  for (const v of vatsInReadingOrder) {
    const start = v.placement.start ?? 1;
    const every = v.placement.every ?? 1;
    if (v.remaining > 0 && tick >= start && (tick - start) % every === 0) {
      v.remaining -= 1;
      blobs.push({
        id: nextBlobId++,
        color: v.placement.color ?? MUD,
        units: 1,
        x: v.placement.x,
        y: v.placement.y,
        entry: null,
        exit: rotateSide('S', v.placement.rotation),
      });
    }
  }
  state.nextBlobId = nextBlobId;

  // ---------------------------------------------------------------- PHASE 2
  // ADVANCE. All movement is simultaneous: every decision below reads the
  // positions as they were at the start of the phase.
  const moving = blobs.filter((b) => b.exit !== null).sort((a, b) => a.id - b.id);
  const origin = new Map<number, string>();
  for (const b of moving) origin.set(b.id, key(b.x, b.y));

  const targets = new Map<number, { x: number; y: number; entry: Side }>();
  for (const b of moving) {
    const exit = b.exit as Side;
    const d = delta(exit);
    const tx = b.x + d.dx;
    const ty = b.y + d.dy;
    const entrySide = opposite(exit);
    const target = tileAt(state, tx, ty);

    // docs/02 3.2: off-grid, empty, wall, or no in/both port on the entry side.
    const inGrid =
      tx >= 0 && ty >= 0 && tx < level.grid.width && ty < level.grid.height;
    if (expandable && inGrid && !target) {
      // An empty cell inside the grid is a decision point, not a failure.
      return { ...prev, status: 'blocked', pending: { x: tx, y: ty, entry: entrySide } };
    }
    if (!target || target.tile === 'wall' || !canEnter(target.tile, target.rotation, entrySide)) {
      return failState(state, { reason: 'leak', x: tx, y: ty, blobIds: [b.id] }, tick);
    }
    targets.set(b.id, { x: tx, y: ty, entry: entrySide });
  }

  // Head-on: A moves P->Q while B moves Q->P. Checked before relocation.
  for (const a of moving) {
    const ta = targets.get(a.id);
    if (!ta) continue;
    for (const b of moving) {
      if (b.id <= a.id) continue;
      const tb = targets.get(b.id);
      if (!tb) continue;
      if (key(ta.x, ta.y) === origin.get(b.id) && key(tb.x, tb.y) === origin.get(a.id)) {
        return failState(state, { reason: 'spill', x: ta.x, y: ta.y, blobIds: [a.id, b.id] }, tick);
      }
    }
  }

  for (const b of moving) {
    const t = targets.get(b.id);
    if (!t) continue;
    const tile = tileAt(state, t.x, t.y);
    if (!tile) continue;
    b.x = t.x;
    b.y = t.y;
    b.entry = t.entry;
    // Pipes route immediately; mixer/splitter/bucket decide in RESOLVE.
    b.exit = pairedPort(tile.tile, tile.rotation, t.entry) ?? null;
  }

  // ---------------------------------------------------------------- PHASE 3
  // COLLIDE. One blob per tile, except a cross (one per lane) and the mixer
  // (handled in RESOLVE, which can spill on 3+).
  const byCell = new Map<string, Blob[]>();
  for (const b of blobs) {
    const k = key(b.x, b.y);
    const list = byCell.get(k);
    if (list) list.push(b);
    else byCell.set(k, [b]);
  }
  for (const [k, list] of byCell) {
    if (list.length <= 1) continue;
    const first = list[0]!;
    const tile = tileAt(state, first.x, first.y);
    if (!tile) continue;
    if (tile.tile === 'mixer') continue; // RESOLVE decides
    if (tile.tile === 'pipe_cross' && list.length === 2) {
      const lanes = new Set(list.map((b) => crossLane(b.entry ?? 'N')));
      if (lanes.size === 2) continue; // different lanes: legal
    }
    void k;
    return failState(
      state,
      { reason: 'spill', x: first.x, y: first.y, blobIds: list.map((b) => b.id).sort((a, b) => a - b) },
      tick,
    );
  }

  // ---------------------------------------------------------------- PHASE 4
  // RESOLVE. Tiles in reading order.
  const consumed = new Set<number>();
  const created: Blob[] = [];
  for (let y = 0; y < level.grid.height; y++) {
    for (let x = 0; x < level.grid.width; x++) {
      const tile = tileAt(state, x, y);
      if (!tile) continue;
      const here = blobs.filter((b) => b.x === x && b.y === y && !consumed.has(b.id));
      if (here.length === 0) continue;

      if (tile.tile === 'mixer') {
        if (here.length >= 3) {
          return failState(
            state,
            { reason: 'spill', x, y, blobIds: here.map((b) => b.id) },
            tick,
          );
        }
        if (here.length === 2) {
          const [a, b] = here as [Blob, Blob];
          const color: ColorId = mix(level.mode, a.color, b.color);
          for (const c of here) consumed.add(c.id);
          created.push({
            id: nextBlobId++,
            color,
            units: a.units + b.units,
            x,
            y,
            entry: null,
            exit: rotateSide('S', tile.rotation), // outlet
          });
        } else {
          // Exactly one blob: hold it. It does not move until a partner comes.
          here[0]!.exit = null;
        }
      } else if (tile.tile === 'splitter') {
        const b = here[0]!;
        const k = key(x, y);
        const next = splitters[k] ?? 'cw';
        // cw = 90 deg clockwise from the inlet, ccw = counter-clockwise.
        b.exit = rotateSide(next === 'cw' ? 'E' : 'W', tile.rotation);
        splitters[k] = next === 'cw' ? 'ccw' : 'cw';
      } else if (tile.tile === 'bucket') {
        const b = here[0]!;
        const order = tile.order;
        if (b.color === MUD) {
          return failState(state, { reason: 'mud', x, y, blobIds: [b.id] }, tick);
        }
        if (!order || b.color !== order.color) {
          return failState(state, { reason: 'wrong_color', x, y, blobIds: [b.id] }, tick);
        }
        const bucket = buckets.find((bk) => bk.placement.x === x && bk.placement.y === y);
        if (bucket) {
          bucket.received += b.units;
          if (bucket.received > order.cans) {
            return failState(state, { reason: 'overfill', x, y, blobIds: [b.id] }, tick);
          }
        }
        consumed.add(b.id);
      }
    }
  }
  blobs = blobs.filter((b) => !consumed.has(b.id)).concat(created);
  state.blobs = blobs;
  state.nextBlobId = nextBlobId;

  // ---------------------------------------------------------------- PHASE 5
  // CHECK: win, then stuck, then timeout.
  const allBucketsFull = buckets.every((b) => b.received === (b.placement.order?.cans ?? 0));
  const allVatsDrained = vats.every((v) => v.remaining === 0);
  if (allBucketsFull && allVatsDrained && blobs.length === 0) {
    return { ...state, status: 'won', tick };
  }

  const seen = new Set(prev.seen);
  const fp = fingerprint(state, tick);
  if (seen.has(fp)) {
    return failState(state, { reason: 'stuck', x: -1, y: -1, blobIds: [] }, tick);
  }
  seen.add(fp);
  state.seen = seen;

  if (tick >= level.maxTicks) {
    return failState(state, { reason: 'timeout', x: -1, y: -1, blobIds: [] }, tick);
  }

  return state;
}

/** Run to completion. Returns the final state. */
export function run(level: Level, placements: Placement[], limit = 500): SimState {
  let s = createSim(level, placements);
  let guard = 0;
  while (s.status === 'running' && guard++ < limit) s = step(s);
  return s;
}

export type { Rotation, Side };
